import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, CheckCircle2, ChevronDown, ChevronRight, FileWarning } from 'lucide-react';
import { api } from '@/lib/api';
import { cn, numero } from '@/lib/utils';
import { EstadoErro } from '@/componentes/ui/estados';
import { Cartao, Esqueleto } from '@/componentes/ui/primitivos';
import { GavetaPendencia } from './GavetaPendencia';

/**
 * O painel que ocupa a tela antes de escolher um recebimento.
 *
 * A tela abria vazia esperando um número, mas quem chega nela raramente sabe o
 * número — sabe que "tem coisa parada". Aqui estão os cinco buracos possíveis,
 * na ORDEM DO FLUXO, porque é assim que se lê:
 *
 *   nota de compra → recebimento → conferência → aprovação → etiqueta → alocação
 *
 * Cada linha é um elo que ficou para trás. Nota sem recebimento é mercadoria
 * que entrou no fiscal e não no físico; etiqueta sem alocação é volume
 * impresso que ninguém guardou na prateleira.
 *
 * SEM POLUIR
 * ----------
 * Linha com zero fica apagada e sem exemplos: quem olha precisa achar o que
 * está ERRADO em um segundo, e cinco números grandes competindo entre si não
 * deixam. Os exemplos são só os mais recentes, e existem para reconhecer — o
 * número puxa o recebimento direto.
 */

type Chave = 'notaSemRecebimento' | 'semConferencia' | 'semAprovacao' | 'semEtiqueta' | 'semAlocacao';

interface ExemploPendencia {
  numero: number;
  data: string | null;
  descricao: string | null;
}

interface Pendencia {
  chave: Chave;
  total: number;
  exemplos: ExemploPendencia[];
}

interface Resposta {
  empresa: number;
  dias: number;
  pendencias: Pendencia[];
}

const TEXTO: Record<Chave, { titulo: string; explica: string; abre: 'recebimento' | 'nota' }> = {
  notaSemRecebimento: {
    titulo: 'Notas de compra sem recebimento',
    explica: 'Operação fiscal 4 lançada que nunca virou recebimento físico.',
    abre: 'nota',
  },
  semConferencia: {
    titulo: 'Recebimentos sem conferência',
    explica: 'Chegou e o coletor ainda não contou nada.',
    abre: 'recebimento',
  },
  semAprovacao: {
    titulo: 'Conferidos sem aprovação',
    explica: 'O coletor contou e ninguém aprovou — a alocação fica travada.',
    abre: 'recebimento',
  },
  semEtiqueta: {
    titulo: 'Aprovados sem etiqueta',
    explica: 'Aprovado, mas as etiquetas de alocação não foram geradas.',
    abre: 'recebimento',
  },
  semAlocacao: {
    titulo: 'Etiquetas Recebimento sem alocação',
    explica: 'Volume com etiqueta impressa que o coletor não guardou.',
    abre: 'recebimento',
  },
};

function dataCurta(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export function PainelPendencias({
  empresa,
  centro,
  aoAbrirRecebimento,
}: {
  empresa: number | null;
  centro: number | null;
  aoAbrirRecebimento: (recebimento: number) => void;
}): JSX.Element {
  const [aberta, definirAberta] = React.useState<Chave | null>(null);

  const consulta = useQuery<Resposta>({
    queryKey: ['recebimento-pendencias', empresa],
    queryFn: () => api.get<Resposta>('/api/recebimentos/pendencias', { empresa, dias: 60 }),
    enabled: empresa !== null,
    staleTime: 120_000,
  });

  if (empresa === null) {
    return (
      <Cartao className="grid min-h-[240px] place-items-center px-6 text-center">
        <p className="text-[13px] text-textoFraco">Escolha uma empresa no seletor do topo.</p>
      </Cartao>
    );
  }

  if (consulta.isLoading) return <Esqueleto className="h-[320px]" />;
  if (consulta.isError) {
    return (
      <Cartao className="p-4">
        <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
      </Cartao>
    );
  }

  const linhas = consulta.data?.pendencias ?? [];
  const limpo = linhas.every((l) => l.total === 0);

  return (
    <Cartao className="relative overflow-hidden">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-borda px-4 py-2.5">
        <span className="text-[13px] font-bold">Pendências do recebimento</span>
        <span className="text-[11.5px] text-textoFraco">
          empresa {consulta.data?.empresa} · últimos {consulta.data?.dias} dias
        </span>
      </div>

      {limpo ? (
        <div className="flex items-center gap-3 px-4 py-8">
          <CheckCircle2 className="h-7 w-7 shrink-0 text-sucesso" />
          <div>
            <p className="text-[14px] font-bold">Nenhuma pendência</p>
            <p className="text-[12.5px] text-textoFraco">
              Todo recebimento desta empresa está conferido, aprovado, etiquetado e guardado.
            </p>
          </div>
        </div>
      ) : (
        <ul className="divide-y divide-borda">
          {linhas.map((linha) => {
            const t = TEXTO[linha.chave];
            const vazio = linha.total === 0;
            return (
              <li
                key={linha.chave}
                className={cn('flex items-start gap-4 px-4 py-3', vazio && 'opacity-45')}
              >
                <span
                  className={cn(
                    'tabular w-[54px] shrink-0 text-right font-mono text-[22px] font-bold leading-none',
                    vazio ? 'text-textoFraco' : 'text-atencao',
                  )}
                >
                  {numero(linha.total)}
                </span>

                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-semibold leading-tight">{t.titulo}</p>
                  <p className="mt-0.5 text-[11.5px] text-textoFraco">{t.explica}</p>

                  {/* Exemplos só quando há o que mostrar — zero não tem exemplo. */}
                  {linha.exemplos.length > 0 ? (
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      {linha.exemplos.map((e) =>
                        t.abre === 'recebimento' ? (
                          <button
                            key={e.numero}
                            type="button"
                            onClick={() => aoAbrirRecebimento(e.numero)}
                            title={e.descricao ?? ''}
                            className="tabular inline-flex items-center gap-1 rounded-lg border border-borda bg-superficie2 px-2 py-0.5 font-mono text-[11.5px] font-bold transition-colors hover:border-primaria hover:text-primaria"
                          >
                            {e.numero}
                            <span className="font-sans font-normal text-textoFraco">
                              {dataCurta(e.data)}
                            </span>
                          </button>
                        ) : (
                          /* Nota de compra não abre aqui: o recebimento dela
                             ainda não existe, e é no Procfit que se lança. */
                          <span
                            key={e.numero}
                            title={e.descricao ?? ''}
                            className="tabular inline-flex items-center gap-1 rounded-lg border border-borda bg-superficie2 px-2 py-0.5 font-mono text-[11.5px] text-textoFraco"
                          >
                            NF {e.numero}
                            <span className="font-sans">{dataCurta(e.data)}</span>
                          </span>
                        ),
                      )}
                      {/*
                        O "+83" e a porta para a lista inteira: cinco exemplos
                        servem para reconhecer, quem vai AGIR precisa de todos.
                      */}
                      {linha.total > linha.exemplos.length ? (
                        <button
                          type="button"
                          onClick={() =>
                            definirAberta((a) => (a === linha.chave ? null : linha.chave))
                          }
                          className="inline-flex items-center gap-0.5 rounded-lg border border-primaria/40 bg-primaria-suave px-2 py-0.5 text-[11px] font-bold text-primaria transition-colors hover:border-primaria"
                        >
                          {aberta === linha.chave ? (
                            <ChevronDown className="h-3 w-3" />
                          ) : (
                            <ChevronRight className="h-3 w-3" />
                          )}
                          +{numero(linha.total - linha.exemplos.length)}
                        </button>
                      ) : null}
                    </div>
                  ) : null}

                  {aberta === linha.chave ? (
                    <GavetaPendencia
                      empresa={empresa}
                      centro={centro}
                      chave={linha.chave}
                      total={linha.total}
                      aoAbrirRecebimento={t.abre === 'recebimento' ? aoAbrirRecebimento : null}
                      aoFechar={() => definirAberta(null)}
                    />
                  ) : null}
                </div>

                {t.abre === 'nota' && !vazio ? (
                  <FileWarning className="mt-1 h-4 w-4 shrink-0 text-textoFraco" />
                ) : !vazio ? (
                  <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-textoFraco" />
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </Cartao>
  );
}
